import React, { FC, useRef } from "react";
import { template } from "lodash";

import {
    Button,
    Checkbox,
    Heading,
    HStack,
    IconButton,
    Message,
    Popover,
    SelectPicker,
    Stack,
    Table as RsuiteTable,
    TableProps,
    TagPicker,
    Text,
    toaster,
    Tooltip,
    Whisper,
} from "rsuite";
import { ItemDataType } from "rsuite/esm/internals/types";
import { ToastContainerProps } from "rsuite/esm/toaster/ToastContainer";

import TrashIcon from "@rsuite/icons/Trash";
import HelpOutlineIcon from "@rsuite/icons/esm/react/HelpOutline";

import { useTranslation } from "react-i18next";

import constants from "utils/constants";

import {
    CustomField,
    CustomFieldType,
    ExcludedLineType,
    InvoiceType,
    Separator,
    TtpMessageSourceField,
} from "store/environments/tenants/custom-fields/erp-types";
import {
    customFieldTypes,
    generateId,
    getInvoiceKeyPrefix,
    getTableKeyPrefix,
} from "../../custom-fields-helpers";

import LineTypesPicker from "./LineTypesPicker/LineTypesPicker";
import SourceFieldPicker from "./SourceFieldPicker/SourceFieldPicker";

import "./Table.less";

export type TableItem<T> = T & {
    isValid?: boolean;
    errors?: Record<string, string> | null;
};

export type TableColumn = keyof CustomField;

const columns: Record<
    keyof Omit<
        CustomField,
        "id" | "targetMethod" | "invoiceType" | "sourcePattern"
    >,
    TableColumn
> = {
    enabled: "enabled",
    targetTable: "targetTable",
    targetField: "targetField",
    type: "type",
    separator: "separator",
    excludedLineTypes: "excludedLineTypes",
    ttpMessageSourceFields: "ttpMessageSourceFields",
};

interface Props extends TableProps {
    erpType: string;
    invoiceType: InvoiceType;
    customFields: TableItem<CustomField>[];
    targetTables: ReadonlyMap<string, string>;
    targetFields: ReadonlyMap<string, string>;
    lineTypes?: ReadonlyArray<ExcludedLineType> | null;
    separators?: ReadonlyArray<Separator> | null;
    ttpMessageSourceFields?: ReadonlyMap<string, TtpMessageSourceField> | null;
    types?: ReadonlyArray<CustomFieldType> | null;
    onRowChange?: (row: TableItem<CustomField>) => void;
    onRowDelete?: (row: TableItem<CustomField>) => void;
    onRowAdded?: (row: TableItem<CustomField>) => void;
}

const Table: FC<Props> = ({
    erpType,
    invoiceType,
    customFields,
    targetTables,
    targetFields,
    lineTypes,
    separators,
    ttpMessageSourceFields,
    types,
    onRowChange,
    onRowDelete,
    onRowAdded,
    ...props
}: Props) => {
    const { t } = useTranslation();

    const ref = useRef<HTMLDivElement>(null);
    const scrollPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    const headerHeight = 40;
    const rowHeight = 46;

    const customFieldsData = customFields.filter(
        (field) => field.invoiceType === invoiceType
    );

    const invoiceTargetTables: ReadonlyArray<string> = Array.from(
        targetTables ?? []
    )
        ?.filter(([key]) => {
            const prefix = getInvoiceKeyPrefix(invoiceType);

            return key.startsWith(prefix);
        })
        ?.map(([, value]) => value);

    const targetTablesData: ItemDataType<string>[] = invoiceTargetTables
        ?.map((value) => ({
            label: value,
            value: value,
        }))
        ?.sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
        );

    const lineTypesData: ItemDataType<any>[] =
        lineTypes
            ?.map((lineType) => ({
                label: lineType.label,
                value: lineType,
            }))
            ?.sort((a, b) =>
                a.label.localeCompare(b.label, undefined, {
                    sensitivity: "base",
                })
            ) ?? [];

    const separatorsData: ItemDataType<any>[] =
        separators?.map((separator) => ({
            label: separator.label,
            value: separator.separator,
        })) ?? [];

    const invoiceTtpMessageSourceFields: ReadonlyArray<TtpMessageSourceField> | null =
        Array.from(ttpMessageSourceFields ?? [])
            ?.filter(([key]) => {
                const prefix = getInvoiceKeyPrefix(invoiceType);

                return key.startsWith(prefix);
            })
            ?.map(([, value]) => value);

    const ttpMessageSourceFieldsData: ItemDataType<any>[] =
        invoiceTtpMessageSourceFields
            ?.map((value) => ({
                label: value.field,
                value: value.field,
            }))
            ?.sort((a, b) =>
                a.label.localeCompare(b.label, undefined, {
                    sensitivity: "base",
                })
            ) ?? [];

    const getTargetFieldsData = (targetTable?: string | null) => {
        if (!targetTable) {
            return [];
        }

        return Array.from(targetFields ?? [])
            ?.filter(([key]) => {
                const prefix = getTableKeyPrefix(targetTable);

                return key.startsWith(prefix);
            })
            ?.map(([, value]) => ({
                label: value,
                value: value,
            }))
            ?.sort((a, b) =>
                a.label.localeCompare(b.label, undefined, {
                    sensitivity: "base",
                })
            );
    };

    const getTitle = (invoiceType: InvoiceType) => {
        switch (invoiceType) {
            case InvoiceType.Po:
                return t("tenantConfig.customFieldsPage.poHeader");
            case InvoiceType.NonPo:
                return t("tenantConfig.customFieldsPage.nonPoHeader");
            default:
                return t("unknown");
        }
    };

    const title = getTitle(invoiceType);

    const toastOptions: ToastContainerProps = {
        duration: constants.alertDurationMsec,
    };

    const handleChange = (
        name: keyof CustomField,
        value:
            | string
            | number
            | boolean
            | null
            | undefined
            | CustomFieldType
            | ExcludedLineType[]
            | TtpMessageSourceField[],
        rowData: CustomField
    ) => {
        if (value === undefined) {
            return;
        }

        const updated = { ...rowData, [name]: value };

        if (name === "targetTable") {
            updated.targetField = null;
        }

        onRowChange?.({ ...updated });
    };

    const handleDelete = (value: CustomField) => {
        onRowDelete?.(value);
    };

    const handleAdd = () => {
        if (customFields.length >= constants.maxInvoiceRowCountInCustomField) {
            toaster.push(
                <Message type="error" showIcon>
                    {t("tenantConfig.customFieldsPage.maxRowLimitReached")}
                </Message>,
                toastOptions
            );

            return;
        }

        const added: TableItem<CustomField> = {
            id: generateId(),
            enabled: true,
            invoiceType: invoiceType,
            type: CustomFieldType.CustomField1,
            separator: 0,
            excludedLineTypes: [],
        };

        if (ttpMessageSourceFields) {
            added.ttpMessageSourceFields = [];
        }

        onRowAdded?.({ ...added });
    };

    return (
        <div ref={ref} className={"custom_fields__table"}>
            <HStack
                style={{ marginBottom: "1rem" }}
                justifyContent={"space-between"}
                spacing={10}
            >
                <Stack.Item alignSelf={"center"} grow={1}>
                    <Heading level={3}>{title}</Heading>
                </Stack.Item>

                <Stack.Item alignSelf={"center"}>
                    <Button
                        className={"custom-fields__table__btn"}
                        appearance="ghost"
                        onClick={() => handleAdd()}
                    >
                        {t("tenantConfig.customFieldsPage.addRow")}
                    </Button>
                </Stack.Item>
            </HStack>

            <RsuiteTable
                data={customFieldsData}
                headerHeight={headerHeight}
                rowHeight={rowHeight}
                autoHeight={true}
                onScroll={(x, y) => {
                    scrollPosition.current = { x, y };
                }}
                shouldUpdateScroll={() => scrollPosition.current}
                {...props}
            >
                <RsuiteTable.Column width={60} fixed>
                    <RsuiteTable.HeaderCell>
                        {t(`tenantConfig.customFieldsPage.table.active`)}
                    </RsuiteTable.HeaderCell>
                    <RsuiteTable.Cell dataKey={columns.enabled}>
                        {(rowData: TableItem<CustomField>) => (
                            <Checkbox
                                checked={rowData.enabled}
                                onChange={(_, checked) => {
                                    handleChange(
                                        columns.enabled,
                                        checked,
                                        rowData
                                    );
                                }}
                            />
                        )}
                    </RsuiteTable.Cell>
                </RsuiteTable.Column>
                <RsuiteTable.Column flexGrow={0.75} minWidth={220}>
                    <RsuiteTable.HeaderCell>
                        {template(
                            t(`tenantConfig.customFieldsPage.table.targetTable`)
                        )({
                            erpType: erpType,
                        })}
                        <sup className={"custom_fields__sub"}>*</sup>
                    </RsuiteTable.HeaderCell>
                    <RsuiteTable.Cell dataKey={columns.targetTable}>
                        {(rowData: TableItem<CustomField>) => (
                            <Whisper
                                trigger="none"
                                placement={"bottom"}
                                container={() => ref.current!}
                                open={!!rowData.errors?.targetTable}
                                speaker={
                                    <Popover className="editable-cell__error-text">
                                        {rowData.errors?.targetTable}
                                    </Popover>
                                }
                            >
                                <div className="custom_fields__table__column__picker">
                                    <SelectPicker
                                        searchable={true}
                                        container={() => ref.current!}
                                        placeholder={template(
                                            t(
                                                "tenantConfig.customFieldsPage.table.targetTablePlaceholder"
                                            )
                                        )({
                                            erpType: erpType,
                                        })}
                                        data={targetTablesData}
                                        value={rowData.targetTable}
                                        onChange={(value) =>
                                            handleChange(
                                                columns.targetTable,
                                                value,
                                                rowData
                                            )
                                        }
                                    />
                                </div>
                            </Whisper>
                        )}
                    </RsuiteTable.Cell>
                </RsuiteTable.Column>
                <RsuiteTable.Column flexGrow={0.75} minWidth={220}>
                    <RsuiteTable.HeaderCell>
                        <Text>
                            {t(
                                `tenantConfig.customFieldsPage.table.targetField`
                            )}
                            <sup className={"custom_fields__sub"}>*</sup>

                            <Whisper
                                container={() => ref.current!}
                                placement="bottom"
                                trigger="hover"
                                speaker={
                                    <Tooltip>
                                        {t(
                                            "tenantConfig.customFieldsPage.validation.targetField"
                                        )}
                                    </Tooltip>
                                }
                            >
                                <HelpOutlineIcon
                                    className={"custom_fields__form__help"}
                                />
                            </Whisper>
                        </Text>
                    </RsuiteTable.HeaderCell>
                    <RsuiteTable.Cell dataKey={columns.targetField}>
                        {(rowData: TableItem<CustomField>) => (
                            <Whisper
                                trigger="none"
                                placement="bottom"
                                container={() => ref.current!}
                                open={!!rowData.errors?.targetField}
                                speaker={
                                    <Popover className="editable-cell__error-text">
                                        {rowData.errors?.targetField}
                                    </Popover>
                                }
                            >
                                <div className="custom_fields__table__column__picker">
                                    <SelectPicker
                                        className="custom_fields__table__column__picker"
                                        searchable={true}
                                        container={() => ref.current!}
                                        placeholder={t(
                                            "tenantConfig.customFieldsPage.table.targetFieldPlaceholder"
                                        )}
                                        data={getTargetFieldsData(
                                            rowData.targetTable
                                        )}
                                        value={rowData.targetField}
                                        disabled={!rowData.targetTable}
                                        onChange={(value) =>
                                            handleChange(
                                                columns.targetField,
                                                value,
                                                rowData
                                            )
                                        }
                                    />
                                </div>
                            </Whisper>
                        )}
                    </RsuiteTable.Cell>
                </RsuiteTable.Column>
                {ttpMessageSourceFields && (
                    <RsuiteTable.Column flexGrow={1.5} minWidth={250}>
                        <RsuiteTable.HeaderCell>
                            {t(
                                `tenantConfig.customFieldsPage.table.ttpMessageSourceFields`
                            )}
                        </RsuiteTable.HeaderCell>
                        <RsuiteTable.Cell
                            dataKey={columns.ttpMessageSourceFields}
                        >
                            {(rowData: TableItem<CustomField>) => (
                                <TagPicker
                                    className="custom_fields__table__column__tag-picker"
                                    container={() => ref.current!}
                                    searchable={false}
                                    placeholder={t(
                                        "tenantConfig.customFieldsPage.table.ttpMessageSourceFieldsPlaceholder"
                                    )}
                                    data={ttpMessageSourceFieldsData}
                                    value={
                                        rowData.ttpMessageSourceFields?.map(
                                            (item) => item.field
                                        ) ?? []
                                    }
                                    onChange={(value: string[]) =>
                                        handleChange(
                                            columns.ttpMessageSourceFields,
                                            rowData.ttpMessageSourceFields?.filter(
                                                (item) =>
                                                    value.some(
                                                        (v) => v === item.field
                                                    )
                                            ),
                                            rowData
                                        )
                                    }
                                    renderMenu={() => {
                                        return (
                                            <SourceFieldPicker
                                                data={
                                                    invoiceTtpMessageSourceFields
                                                }
                                                value={
                                                    rowData.ttpMessageSourceFields ??
                                                    []
                                                }
                                                onChange={(value) =>
                                                    handleChange(
                                                        columns.ttpMessageSourceFields,
                                                        value,
                                                        rowData
                                                    )
                                                }
                                            />
                                        );
                                    }}
                                />
                            )}
                        </RsuiteTable.Cell>
                    </RsuiteTable.Column>
                )}
                {separators && (
                    <RsuiteTable.Column flexGrow={0.25} minWidth={120}>
                        <RsuiteTable.HeaderCell>
                            {t(`tenantConfig.customFieldsPage.table.separator`)}
                        </RsuiteTable.HeaderCell>
                        <RsuiteTable.Cell dataKey={columns.separator}>
                            {(rowData: TableItem<CustomField>) => (
                                <div className="custom_fields__table__column__picker">
                                    <SelectPicker
                                        className="custom_fields__table__column__picker"
                                        searchable={true}
                                        container={() => ref.current!}
                                        cleanable={false}
                                        placeholder={t(
                                            "tenantConfig.customFieldsPage.table.separatorPlaceholder"
                                        )}
                                        data={separatorsData}
                                        value={rowData.separator}
                                        onChange={(value) =>
                                            handleChange(
                                                columns.separator,
                                                value,
                                                rowData
                                            )
                                        }
                                    />
                                </div>
                            )}
                        </RsuiteTable.Cell>
                    </RsuiteTable.Column>
                )}
                {invoiceType === InvoiceType.NonPo && (
                    <RsuiteTable.Column flexGrow={1.5} minWidth={220}>
                        <RsuiteTable.HeaderCell>
                            {t(
                                `tenantConfig.customFieldsPage.table.excludedLineTypes`
                            )}
                        </RsuiteTable.HeaderCell>
                        <RsuiteTable.Cell dataKey={columns.excludedLineTypes}>
                            {(rowData: TableItem<CustomField>) => (
                                <LineTypesPicker
                                    className="custom_fields__table__column__tag-picker"
                                    container={() => ref.current!}
                                    data={lineTypesData}
                                    value={rowData.excludedLineTypes}
                                    placeholder={t(
                                        "tenantConfig.customFieldsPage.table.excludedLineTypesPlaceholder"
                                    )}
                                    onChange={(value) =>
                                        handleChange(
                                            columns.excludedLineTypes,
                                            value,
                                            rowData
                                        )
                                    }
                                />
                            )}
                        </RsuiteTable.Cell>
                    </RsuiteTable.Column>
                )}
                {types && (
                    <RsuiteTable.Column flexGrow={0.75} minWidth={200}>
                        <RsuiteTable.HeaderCell>
                            {t(
                                `tenantConfig.customFieldsPage.table.customFieldType`
                            )}
                        </RsuiteTable.HeaderCell>
                        <RsuiteTable.Cell dataKey={columns.type}>
                            {(rowData: TableItem<CustomField>) => (
                                <div className="custom_fields__table__column__picker">
                                    <SelectPicker
                                        container={() => ref.current!}
                                        searchable={false}
                                        cleanable={false}
                                        data={customFieldTypes}
                                        value={rowData.type}
                                        onChange={(value) =>
                                            handleChange(
                                                columns.type,
                                                value,
                                                rowData
                                            )
                                        }
                                    />
                                </div>
                            )}
                        </RsuiteTable.Cell>
                    </RsuiteTable.Column>
                )}
                <RsuiteTable.Column width={100}>
                    <RsuiteTable.HeaderCell>
                        {t(`tenantConfig.customFieldsPage.table.action`)}
                    </RsuiteTable.HeaderCell>
                    <RsuiteTable.Cell>
                        {(rowData: TableItem<CustomField>) => (
                            <IconButton
                                appearance="subtle"
                                size="sm"
                                onClick={() => {
                                    handleDelete(rowData);
                                }}
                                icon={<TrashIcon />}
                            />
                        )}
                    </RsuiteTable.Cell>
                </RsuiteTable.Column>
            </RsuiteTable>
        </div>
    );
};

export default Table;
